// A small, safe Markdown renderer for AI answers: paragraphs, headings,
// bullet / numbered lists, tables, **bold**, `code`, links. Text only (no
// raw HTML), so nothing in an answer can inject markup.
import type { JSX } from "preact";

function inline(s: string, key: string): (string | JSX.Element)[] {
  const out: (string | JSX.Element)[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((https?:\/\/[^)\s]+)\))/g;
  let last = 0, m: RegExpExecArray | null, i = 0;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    const x = m[0];
    if (x.startsWith("**")) out.push(<b key={key + i++}>{x.slice(2, -2)}</b>);
    else if (x.startsWith("`")) out.push(<code key={key + i++}>{x.slice(1, -1)}</code>);
    else { const t = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(x)!; out.push(<a key={key + i++} href={t[2]} target="_blank" rel="noreferrer">{t[1]}</a>); }
    last = m.index + x.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

export function Markdown({ text }: { text: string }) {
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks: JSX.Element[] = [];
  let i = 0, k = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (!l.trim()) { i++; continue; }
    if (/^\|.*\|\s*$/.test(l) && i + 1 < lines.length && /^\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const cells = (r: string) => r.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = cells(l); i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\|.*\|\s*$/.test(lines[i])) rows.push(cells(lines[i++]));
      blocks.push(<div key={k++} style="overflow-x:auto"><table class="t"><thead><tr>{head.map((h, j) => <th key={j}>{inline(h, "h" + j)}</th>)}</tr></thead><tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, j) => <td key={j}>{inline(c, `c${ri}-${j}`)}</td>)}</tr>)}</tbody></table></div>);
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(l);
    if (h) { blocks.push(<h3 key={k++} style="margin:12px 0 6px">{inline(h[2], "t" + k)}</h3>); i++; continue; }
    if (/^\s*([-*]|\d+\.)\s+/.test(l)) {
      const ordered = /^\s*\d+\./.test(l); const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*([-*]|\d+\.)\s+/, ""));
      const L = ordered ? "ol" : "ul";
      blocks.push(<L key={k++} style="margin:0 0 10px;padding-left:20px">{items.map((x, j) => <li key={j}>{inline(x, "l" + j)}</li>)}</L>);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\s*([-*]|\d+\.)\s|\|)/.test(lines[i])) para.push(lines[i++]);
    blocks.push(<p key={k++} style="margin:0 0 10px;line-height:1.55">{inline(para.join(" "), "p" + k)}</p>);
  }
  return <>{blocks}</>;
}
